import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import Modal from "../../../../components/modal";
import AppDatePicker from "../../../../components/form/base/app-datepicker";
import { StatusType } from "../../../../types/statistics.types";
import { LabelValue } from "../../../../types/label-value.types";
import AppSelect from "../../../../components/form/base/app-select";
import { useSelector } from "react-redux";
import { AppStore } from "../../../../store/store";
import { UserState } from "../../../../store/user.slice";
import { apiService } from "../../../../services/api.service";
import dateService from "../../../../services/date.service";
import { ReactComponent as CardIcon } from '../../../../assets/icons/processor-card.svg'
import { ReactComponent as UserIcon } from '../../../../assets/icons/user.svg';
import { ReactComponent as MerchantIcon } from '../../../../assets/icons/merchant.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete.svg';
import { ReactComponent as SearchIcon } from '../../../../assets/icons/search.svg';
import { formatAmount } from "../../../../utils/number-utils";
import AppCheckboxInput from "../../../../components/form/base/app-checkbox-input";
import TableContent from "../../../../components/table-content";
import TablePagination from "../../../../components/table-pagination";
import AppInput from "../../../../components/form/base/app-input";
import { CustomTransaction } from "../../../../types/transaction.types";
import { CommissionType } from "../../merchants/edit-mid/edit-mid.types";
import { ApiPageInfo } from "../../../../services/api.service-types";
import AppTimePicker from "../../../../components/form/base/app-time-picker";
import AppFormElementLabel from "../../../../components/form/base/app-form-element-label";

interface AddCustomTransactionsModalProps {
    addedTransactions: CustomTransaction[];
    commissionsOptions: LabelValue[];
    merchantId: string;
    midId: string;
    setAddedTransaction: (transaction: CustomTransaction[]) => void;
    handleCancel: () => void;
    handleSubmit: () => void;
    addSingleTransaction: (addTransaction: CustomTransaction) => void;
}

const AddCustomTransactionsModal: React.FC<AddCustomTransactionsModalProps> = ({ addedTransactions, commissionsOptions, merchantId, midId, setAddedTransaction, handleCancel, handleSubmit, addSingleTransaction }) => {

    const { user } = useSelector<AppStore, UserState>((state) => state.user)

    const [selectedFromDate, setSelectedFromDate] = useState<Dayjs | null>();
    const [selectedFromTime, setSelectedFromTime] = useState<string | null>('00:00');
    const [selectedToDate, setSelectedToDate] = useState<Dayjs | null>();
    const [selectedToTime, setSelectedToTime] = useState<string | null>('00:00');
    const [selectedStatus, setSelectedStatus] = useState<StatusType>();

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState<number>();

    const [customTransactions, setCustomTransactions] = useState<CustomTransaction[]>([]);

    const [transactionsLoader, setTransactionsLoader] = useState(false);

    const [searchValue, setSearchValue] = useState<string>('');

    useEffect(() => {
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    useEffect(() => {
        const pageInfo = {
            page: currentPage - 1,
            size: 5
        }
        setTransactionsLoader(true)
        apiService.getTransactionsListForSettlement({
            traceId: searchValue,
            startDate: dateService.formatDateTime(selectedFromDate || null, selectedFromTime),
            endDate: dateService.formatDateTime(selectedToDate || null, selectedToTime),
            merchantId: merchantId,
            midId: midId,
            statuses: selectedStatus ? [selectedStatus] : Object.values(StatusType).filter(status => status !== (StatusType.APPROVED || StatusType.DECLINED))
        }, pageInfo)
            .then((response) => {
                setCustomTransactions(response.data.content)
                setTotalPages(response.data.totalPages)
                setTotalElements(response.data.totalElements)
            })
            .catch((error) => console.log(error))
            .finally(() => setTransactionsLoader(false))

    }, [user, selectedFromDate, selectedFromTime, selectedToDate, selectedToTime, searchValue, selectedStatus])

    const statusOptions: LabelValue[] = Object.values(StatusType).map((status) => ({
        label: status,
        value: status
    }))


    const columnAddedTransactions = [
        {
            Header: 'Trace ID',
            accessor: 'id',
            Cell: ({ value }: { value: string }) => (
                <div>
                    <span
                    >{value}</span>
                </div>
            ),
        },
        {
            Header: 'Customer',
            accessor: 'customerName',
            Cell: ({ value, row }: { value: string; row: any }) => (
                <div className="flex-column flex-gap-5">
                    <div className="flex-row flex-gap-5 flex-align-start-center">
                        <UserIcon />
                        <span>{value}</span>
                    </div>
                    <div className="secondary-text accent-gradient-text">
                        {row.original.customerCardType} xxxx xxxx xxxx {row.original.customerLastFourCardDigits}
                    </div>
                </div>
            ),
        },
        {
            Header: 'Merchant',
            accessor: 'merchantName',
            Cell: ({ value, row }: { value: string; row: any }) => (
                <div className="flex-column flex-gap-5">
                    <div className="flex-row flex-gap-5 flex-align-start-center">
                        <MerchantIcon />
                        <span>{value}</span>
                    </div>
                    <div className="secondary-text accent-gradient-text">{row.original.transactionReferenceCode}</div>
                </div>
            ),
        },
        {
            Header: 'Channel',
            accessor: 'processorName',
            Cell: ({ value, row }: { value: string; row: any }) => (

                <div className="flex-column flex-gap-5">
                    <div className="flex-row flex-gap-5 flex-align-start-center">
                        <CardIcon />
                        <span>{value}</span>
                    </div>
                    <div className="secondary-text accent-gradient-text">
                        {row.original.processorTransactionId}
                    </div>
                </div>
            ),
        },
        {
            Header: 'Value',
            accessor: 'amount',
            Cell: ({ value }: { value: string }) =>
                <span className="value-text accent-gradient-text">${formatAmount(+value)}</span>
        },
        {
            Header: () => (
                <div className='flex-row flex-align-center-center'>
                    Status
                </div>
            ),
            accessor: 'status',
            Cell: ({ value, row }: { value: string; row: any }) => {


                return (
                    <div className='flex-row flex-align-center-center width-100'>
                        <div
                            className={`status ${value?.toLowerCase()} flex-row flex-align-center-center`}
                        >
                            <span
                            >{value}</span>
                        </div>

                    </div>
                );
            },
        },
        {
            Header: () => (
                <div className='flex-row flex-align-center-center'>
                    Time
                </div>
            ),
            accessor: 'date',
            Cell: ({ value }: { value: string }) =>
                <div className="date-text">{dateService.formatDateForTable(value)}</div>
        },
        {
            Header: 'Selected Fee',
            accessor: 'selectedFee',
            Cell: ({ value, row }: { value: string, row: any }) => {
                if (!customTransactions) return;
                const transaction = customTransactions.find((t: any) => t.id === row.original.id)!;

                return (
                    <div className="select-fee">
                        <AppSelect
                            value={transaction.selectedFee}
                            options={commissionsOptions}
                            onChange={(newValue) => {
                                setCustomTransactions(prev =>
                                    prev.map(t =>
                                        t.id === row.original.id
                                            ? { ...t, selectedFee: newValue as CommissionType }
                                            : t
                                    )
                                );
                            }}
                        />
                    </div>)
            }
        },
        {
            Header: 'Add to Settlement',
            accessor: '',
            Cell: ({ row }: { row: any }) => {
                const isIncluded = addedTransactions.some((tx: CustomTransaction) => tx.id === row.original.id);

                return (
                    <div className='checkbox-column flex-row flex-align-center-center width-100'>
                        <AppCheckboxInput
                            id={`custom-transactions-${row.original.id}`}
                            label=''
                            checked={isIncluded}
                            onChange={() => {
                                if (row.original.selectedFee) {
                                    addSingleTransaction(row.original)
                                }

                            }}
                        />
                    </div>
                );
            }
        },
    ];

    const handlePageChange = async (newPage: number) => {
        const pageInfo: ApiPageInfo = {
            page: newPage - 1,
            size: 5
        }
        try {
            setTransactionsLoader(true)
            const response = await apiService.getTransactionsListForSettlement({
                traceId: searchValue,
                startDate: dateService.formatDateTime(selectedFromDate!, selectedFromTime),
                endDate: dateService.formatDateTime(selectedToDate!, selectedToTime),
                merchantId: merchantId,
                midId: midId,
                statuses: selectedStatus ? [selectedStatus] : Object.values(StatusType).filter(status => status !== (StatusType.APPROVED || StatusType.DECLINED))
            }, pageInfo)
            setCustomTransactions(response.data.content);
            setCurrentPage(newPage);
        } catch (e) {

        } finally {
            setTransactionsLoader(false)
        }
    }


    return (
        <Modal
            message='Add Custom Transactions'
            onCancel={() => handleCancel()}
            showCancelButton
            buttonText='Done'
            onSubmit={() => handleCancel()}
        >
            <div className='flex-column flex-gap-30 width-100'>
                <div className='flex-row width-100 flex-align-start-end flex-wrap flex-gap-30'>
                    <AppInput
                        theme='filter'
                        icon={<SearchIcon />}
                        value={searchValue}
                        placeholder='Search'
                        onChange={(event) => {
                            setSearchValue(event.target.value)
                        }}
                    />
                    <AppDatePicker
                        value={selectedFromDate}
                        onChange={(newDate) => {
                            setSelectedFromDate(newDate);

                        }}
                        placeholder="Select DAY FROM"
                        shouldDisableDate={(date) =>
                            (selectedToDate && date.isAfter(selectedToDate, 'day')) || date.isAfter(dayjs(), 'day')
                        }
                    />
                    <div className='flex-column'>
                        <AppFormElementLabel name='selectedFromTime' label='Select Time From' id=''></AppFormElementLabel>
                        <AppTimePicker
                            value={selectedFromTime}
                            onChange={(value) => setSelectedFromTime(value)}
                        />
                    </div>
                    <AppDatePicker
                        value={selectedToDate}
                        onChange={(newDate) => {
                            setSelectedToDate(newDate);
                        }}
                        placeholder="Select DAY TO"
                        shouldDisableDate={(date) =>
                            (selectedFromDate && date.isBefore(selectedFromDate, 'day')) || date.isAfter(dayjs(), 'day')
                        }
                    />
                    <div className='flex-column'>
                        <AppFormElementLabel name='selectedToTime' label='Select Time To' id=''></AppFormElementLabel>
                        <AppTimePicker
                            value={selectedToTime}
                            onChange={(value) => setSelectedToTime(value)}
                        />
                    </div>
                    <AppSelect
                        theme='filter'
                        options={statusOptions}
                        value={selectedStatus}
                        placeholder='Select Status'
                        onChange={(value) => { setSelectedStatus(value as StatusType) }}
                    />
                </div>
                <div className='inner-page flex-column flex-gap-30 width-100'>
                    <div className='flex-row width-100 flex-align-space-between-center'>
                        <span className='total-elements'>Total elements: {totalElements}</span>
                    </div>
                    <TableContent columns={columnAddedTransactions} data={customTransactions || []} isLoading={transactionsLoader} />
                    <TablePagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>
        </Modal>
    )
}

export default AddCustomTransactionsModal;